import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Footer, MarketingHeader, SEO } from '@/components';
import {
  LEAVE_BEHIND_DISPLAY_URL,
  LEAVE_BEHIND_FEE_WAIVER_DISPLAY,
  LEAVE_BEHIND_QR,
  type LeaveBehindCopy,
} from '@/lib/leave-behind';
import { track } from '@/lib/analytics';
import shipSvg from '@/assets/ship.svg';
import {
  FiCalendar,
  FiDownload,
  FiDollarSign,
  FiMessageCircle,
} from 'react-icons/fi';

const khula = {
  fontFamily:
    "'Khula', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
} as const;

const nunito = {
  fontFamily:
    "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
} as const;

function usePrintMode(): boolean {
  const [printMode, setPrintMode] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setPrintMode(params.get('print') === '1');
  }, []);
  return printMode;
}

export function LeaveBehindPage({ copy }: { copy: LeaveBehindCopy }) {
  const printMode = usePrintMode();

  useEffect(() => {
    track(copy.screenEvent, { language: copy.htmlLang });
  }, [copy.htmlLang, copy.screenEvent]);

  const handlePrint = () => {
    track('Leave Behind Print Clicked', { language: copy.htmlLang });
    window.print();
  };

  return (
    <div className={printMode ? 'bg-white' : 'min-h-screen bg-gray-100'}>
      <Helmet>
        <html lang={copy.htmlLang} />
      </Helmet>
      <SEO title={copy.title} description={copy.description} path={copy.path} />
      <style>{`
        @page { size: letter; margin: 0; }
        .leave-behind-sheet {
          width: 8.5in;
          height: 11in;
          padding: 0.55in 0.58in;
          box-sizing: border-box;
          background: #fff;
          color: #111827;
          overflow: hidden;
        }
        @media print {
          .leave-behind-chrome { display: none !important; }
          html, body, #root { margin: 0 !important; padding: 0 !important; background: #fff !important; }
          .leave-behind-page { background: #fff !important; }
          .leave-behind-sheet {
            box-shadow: none !important;
            margin: 0 !important;
            page-break-after: avoid;
            page-break-inside: avoid;
          }
        }
      `}</style>

      {printMode ? null : (
        <div className='leave-behind-chrome'>
          <MarketingHeader />
        </div>
      )}

      <div
        className={`leave-behind-page ${
          printMode ? '' : 'pt-28 pb-16 px-4'
        }`}
      >
        {printMode ? null : (
          <div className='leave-behind-chrome mx-auto mb-4 flex max-w-[8.5in] flex-wrap items-center justify-between gap-3'>
            <p className='text-sm text-gray-600' style={nunito}>
              US Letter · one side · photocopy-safe
            </p>
            <div className='flex flex-wrap items-center gap-3'>
              <Link
                to={copy.otherLanguagePath}
                className='text-sm font-semibold text-primary hover:underline'
                style={nunito}
              >
                {copy.otherLanguageLabel}
              </Link>
              <button
                type='button'
                onClick={handlePrint}
                className='rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white'
                style={nunito}
              >
                {copy.printLabel}
              </button>
            </div>
          </div>
        )}

        <LeaveBehindSheet copy={copy} />
      </div>

      {printMode ? null : (
        <div className='leave-behind-chrome'>
          <Footer />
        </div>
      )}
    </div>
  );
}

export function LeaveBehindSheet({ copy }: { copy: LeaveBehindCopy }) {
  const featureIcons = [
    FiMessageCircle,
    FiCalendar,
    FiDollarSign,
    FiDownload,
  ] as const;

  return (
    <article
      className='leave-behind-sheet mx-auto flex flex-col print:shadow-none'
      style={{
        boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
        ...nunito,
      }}
    >
      <header className='flex items-center justify-between border-b border-[#9fb4b8] pb-4'>
        <div className='flex items-center gap-3'>
          <span className='flex h-12 w-12 items-center justify-center rounded-full border border-[#01343c] bg-[#015461]'>
            <img src={shipSvg} alt='' className='h-8 w-8' />
          </span>
          <h1
            className='pt-1 text-[39px] leading-none text-[#1e3a40]'
            style={{ ...khula, fontWeight: 300 }}
          >
            Harbor
          </h1>
        </div>
        <div className='rounded-full border border-[#1e3a40] px-4 py-1.5'>
          <p
            className='text-[10px] font-semibold uppercase tracking-[0.17em] text-[#1e3a40]'
            style={{ ...nunito, fontWeight: 700 }}
          >
            {copy.kicker}
          </p>
        </div>
      </header>

      <section className='relative mt-5 overflow-hidden rounded-xl border border-[#9fb4b8] bg-[#f2f8f8] px-6 py-5'>
        <span className='absolute inset-y-0 left-0 w-1.5 bg-[#eb5e28]' />
        <p
          className='max-w-[6.2in] text-[26px] leading-[1.2] text-[#1e3a40]'
          style={{ ...nunito, fontWeight: 700 }}
        >
          {copy.tagline}
        </p>
        <p
          className='mt-3 inline-flex rounded-full bg-white px-3 py-1 text-[13px] text-[#1e3a40] ring-1 ring-inset ring-[#9fb4b8]'
          style={{ ...nunito, fontWeight: 700 }}
        >
          {copy.startLine}
        </p>
      </section>

      <section className='mt-6'>
        <p
          className='text-[11px] uppercase tracking-[0.16em] text-[#52666a]'
          style={{ ...nunito, fontWeight: 700 }}
        >
          {copy.featuresHeading}
        </p>
        <ul className='mt-3 grid grid-cols-2 gap-3'>
          {copy.tools.map((tool, index) => {
            const Icon = featureIcons[index];
            return (
              <li
                key={tool}
                className='flex min-h-[0.68in] items-center gap-3 rounded-lg border border-[#bac8cb] px-3.5 py-3 text-[14px] leading-snug text-[#1e3a40]'
                style={{ ...nunito, fontWeight: 700 }}
              >
                <span className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#e5f2f3] text-[#015461]'>
                  <Icon className='h-[18px] w-[18px]' aria-hidden='true' />
                </span>
                {tool}
              </li>
            );
          })}
        </ul>
      </section>

      <section className='mt-5 grid grid-cols-[0.88fr_1.12fr] gap-3'>
        <div className='rounded-xl border border-[#1e3a40] px-4 py-4'>
          <p
            className='text-[10px] uppercase tracking-[0.15em] text-[#52666a]'
            style={{ ...nunito, fontWeight: 700 }}
          >
            {copy.pricingHeading}
          </p>
          <p
            className='mt-2 text-[20px] leading-[1.25] text-[#1e3a40]'
            style={{ ...nunito, fontWeight: 700 }}
          >
            {copy.price}
          </p>
        </div>
        <div className='rounded-xl border border-[#bac8cb] bg-[#fbf6ef] px-4 py-4'>
          <p
            className='text-[13px] text-[#1e3a40]'
            style={{ ...nunito, fontWeight: 700 }}
          >
            {copy.feeWaiverHeading}
          </p>
          <p
            className='mt-1 text-[11.5px] leading-relaxed text-[#354e53]'
            style={{ ...nunito, fontWeight: 400 }}
          >
            {copy.feeWaiver}{' '}
            <span className='font-bold'>
              {LEAVE_BEHIND_FEE_WAIVER_DISPLAY}
            </span>
          </p>
        </div>
      </section>

      <section className='mt-5 flex items-center justify-between rounded-xl border-2 border-[#015461] bg-[#eaf5f6] px-5 py-4'>
        <div className='max-w-[4.75in]'>
          <p
            className='text-[12px] uppercase tracking-[0.14em] text-[#015461]'
            style={{ ...nunito, fontWeight: 700 }}
          >
            {copy.getApp}
          </p>
          <p
            className='mt-1 text-[28px] leading-none text-[#1e3a40]'
            style={{ ...nunito, fontWeight: 700 }}
          >
            {LEAVE_BEHIND_DISPLAY_URL}
          </p>
          <p
            className='mt-3 text-[13px] text-[#354e53]'
            style={{ ...nunito, fontWeight: 600 }}
          >
            {copy.scanLabel}
          </p>
        </div>
        <img
          src={LEAVE_BEHIND_QR[copy.htmlLang]}
          alt={`${copy.getApp}: ${LEAVE_BEHIND_DISPLAY_URL}`}
          width={130}
          height={130}
          className='h-[1.35in] w-[1.35in] shrink-0 rounded-md border border-[#1e3a40] bg-white p-1.5'
        />
      </section>

      <footer className='mt-auto border-t border-[#9fb4b8] pt-3'>
        <p
          className='text-[9.5px] leading-relaxed text-[#52666a]'
          style={{ ...nunito, fontWeight: 400 }}
        >
          {copy.disclaimer}
        </p>
      </footer>
    </article>
  );
}
